import type { ProjectDetails } from "@simbelapp/order-sdk";
import { DateChoiceErrorCodeEnum } from "~/utils/enums/date-choice.enums";
import type { IDateChoiceAttendeeCard, IDateChoiceEligibleSession } from "~/utils/interfaces/date-choice-interfaces";

/**
 * `/sessions/training` row → the session card the date-choice screens read. That endpoint serves
 * plain sessions (dates as `Date`, headcount as `nbAttendees`) and knows nothing of `isFull`, which
 * is derived here.
 *
 * "Full" and "not selectable" are two different questions, and date choice used to conflate them:
 * a session at capacity was refused even when it explicitly allowed overbooking (Asana
 * 1218408506018290). The session's own `overBookingNotAllowed` is the judge, exactly as in the
 * catalog registration (`RequestTrainingPageContainer`) and the invite modal. The column is
 * nullable and defaults to `false`, so anything falsy reads as "overbooking allowed".
 *
 * Orders applies that same rule on its three date-choice routes (`choice-options`, `choose-session`
 * and `admin-choose-session`), so a card this helper leaves selectable is one the resolution really
 * accepts: no more card that ticks and then fails on « La dernière place vient d'être prise ».
 *
 * `isFull` stays on the card: the propose screen flags a full session « Complète » whether or not
 * it can still be joined (AC-CDD.12).
 *
 * `date` columns come back as "YYYY-MM-DD", so the ISO round-trip keeps the day intact.
 * `designation` is the session's "Indication", the last field of the card — only this row carries it.
 */
export function toEligibleSession(session: ProjectDetails): IDateChoiceEligibleSession {
  const nbRegistered = session.nbAttendees ?? 0;
  const nbMaxAttendees = session.maxNbAttendees ?? null;
  const isFull = nbMaxAttendees !== null && nbRegistered >= nbMaxAttendees;

  return {
    sessionId: session.sessionId,
    startDate: session.startDate?.toISOString() ?? "",
    endDate: session.endDate?.toISOString() ?? "",
    startTime: session.startTime ?? null,
    endTime: session.endTime ?? null,
    nbRegistered,
    nbMaxAttendees,
    isFull,
    isSelectable: !isFull || !session.overBookingNotAllowed,
    location: session.location ?? null,
    format: session.format ?? null,
    sessionItemType: session.sessionItemType,
    duration: session.duration ?? null,
    designation: session.designation ?? null,
    startTimezone: session.startTimezone ?? null,
    endTimezone: session.endTimezone ?? null,
  };
}

/** Same row, shaped for the participant screen: it carries the refusal reason rather than `isFull`. */
export function toAttendeeCard(session: ProjectDetails): IDateChoiceAttendeeCard {
  const { isFull, ...card } = toEligibleSession(session);
  return {
    ...card,
    notSelectableReason: card.isSelectable ? null : DateChoiceErrorCodeEnum.SESSION_FULL,
  };
}
